/**
 * Core domain types for the profile analysis engine.
 *
 * Nothing under `src/core/` may import React or touch the DOM. That single
 * constraint is what makes the engine unit-testable and lets it run anywhere —
 * browser, Node, or a future CLI. See `docs/ARCHITECTURE.md`.
 */

/* -------------------------------------------------------------------------- */
/* Input                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Who the profile is written for. This reweights the rubric rather than
 * changing which rules exist — see `SECTION_WEIGHTS` in `scoring.ts`.
 */
export type AudienceMode = 'student' | 'jobSeeker' | 'professional' | 'recruiter';

export const AUDIENCE_MODES = ['student', 'jobSeeker', 'professional', 'recruiter'] as const;

/** One role, internship, or project on the profile. */
export interface ExperienceEntry {
  id: string;
  title: string;
  organisation: string;
  /**
   * The description exactly as pasted, newlines intact. Bullets are *derived*
   * from this via `toBullets()` rather than stored, so there is one source of
   * truth and the form stays a plain textarea.
   */
  description: string;
}

/**
 * Profile facts we cannot infer from pasted text and so must ask about.
 * Keep this list short — every entry is a question the user has to answer.
 */
export interface ProfileChecklist {
  hasPhoto: boolean;
  hasBanner: boolean;
  hasCustomUrl: boolean;
  hasLocation: boolean;
  hasFeatured: boolean;
  recommendationCount: number;
  openToWork: boolean;
  hiring: boolean;
}

export interface ProfileInput {
  headline: string;
  about: string;
  experience: ExperienceEntry[];
  skills: string[];
  /** A role track id from `data/roleKeywords.ts`, or `''` for "not specified". */
  targetRole: string;
  checklist: ProfileChecklist;
}

/* -------------------------------------------------------------------------- */
/* Findings                                                                   */
/* -------------------------------------------------------------------------- */

/** The five scored areas of a profile. */
export type SectionId = 'headline' | 'about' | 'experience' | 'skills' | 'completeness';

export const SECTION_IDS = ['headline', 'about', 'experience', 'skills', 'completeness'] as const;

/**
 * How much a finding hurts. Drives both sort order and UI colour.
 * - `critical` — actively costs the user opportunities; fix today.
 * - `important` — a clear, measurable improvement.
 * - `polish`   — worth doing once the above are done.
 */
export type Severity = 'critical' | 'important' | 'polish';

export const SEVERITIES = ['critical', 'important', 'polish'] as const;

/**
 * One specific, actionable problem found on the profile.
 *
 * The `why`/`fix` split is deliberate and enforced by review: users act on
 * advice far more readily when they understand the reason for it.
 */
export interface Finding {
  /** Stable dotted id, e.g. `headline.too-short`. Never reuse or renumber. */
  id: string;
  section: SectionId;
  severity: Severity;
  /** Short, plain-language problem statement. Not a scolding. */
  title: string;
  /** Why this costs the user something. One or two sentences. */
  why: string;
  /** Concretely what to do instead. Imperative voice. */
  fix: string;
  /** The offending text, so the user can see exactly what we matched. */
  evidence?: string[];
}

/* -------------------------------------------------------------------------- */
/* Rules                                                                      */
/* -------------------------------------------------------------------------- */

/** Everything a rule is allowed to look at. */
export interface RuleContext {
  profile: ProfileInput;
  mode: AudienceMode;
  /**
   * Precomputed once per run and shared, because deriving it is the only
   * non-trivial work in the pipeline. Everything else a rule needs it should
   * compute itself from `profile` using helpers in `text.ts`.
   */
  keywords: KeywordAnalysis;
}

export interface RuleOutcome {
  /** 0 = failed outright, 1 = passed. Clamped to [0,1] by the engine. */
  score: number;
  findings?: Finding[];
  /**
   * Set when the rule genuinely cannot judge — for example a keyword rule with
   * no target role selected. Skipped rules are excluded from scoring entirely
   * rather than scored zero, so we never penalise a user for a question they
   * were not asked.
   */
  skipped?: boolean;
}

export interface Rule {
  /** Stable dotted id, matching the `Finding` ids it can emit. */
  id: string;
  section: SectionId;
  /** Relative weight *within* its section. Sections are weighted separately. */
  weight: number;
  /** When set, the rule only runs for these audience modes. */
  appliesTo?: readonly AudienceMode[];
  evaluate(ctx: RuleContext): RuleOutcome;
}

/* -------------------------------------------------------------------------- */
/* Output                                                                     */
/* -------------------------------------------------------------------------- */

export type Grade = 'A' | 'B' | 'C' | 'D' | 'F';

export interface KeywordAnalysis {
  /** Role track that was matched, or `null` when the user picked none. */
  roleId: string | null;
  roleLabel: string | null;
  /** Share of the role's `must` keywords found anywhere on the profile, 0..1. */
  coverage: number;
  present: string[];
  missing: string[];
}

export interface SectionReport {
  section: SectionId;
  label: string;
  /** 0..100, or `null` when every rule in the section was skipped. */
  score: number | null;
  /** This section's share of the overall score, 0..1, after renormalisation. */
  weight: number;
  findings: Finding[];
}

export interface RewriteVariant {
  id: string;
  label: string;
  /** When to prefer this variant over the others. */
  description: string;
  text: string;
}

export interface RewriteSet {
  headline: RewriteVariant[];
  about: RewriteVariant[];
}

export interface Report {
  /** 0..100. */
  overall: number;
  grade: Grade;
  mode: AudienceMode;
  sections: SectionReport[];
  /** Every finding, flattened and sorted worst-first. */
  findings: Finding[];
  /** The three highest-leverage findings, for the "start here" panel. */
  topFixes: Finding[];
  keywords: KeywordAnalysis;
  rewrites: RewriteSet;
  /** ISO timestamp. Injected by the caller so `analyze()` stays pure. */
  generatedAt: string;
}
